import { useRef, useEffect } from 'react';

import { LDFlagValue } from 'launchdarkly-js-client-sdk';

export const LS_FLAG_PREFIX = 'feature-flag-';
export const FF_OVERRIDE_EVENT = 'adsk:ff-override-change';

export const getLocalStorageFlagOverride = <FlagValue,>(
  flagName: string,
): { value: FlagValue; isOverridden: true } | { isOverridden: false } => {
  const stored = globalThis.localStorage.getItem(
    `${LS_FLAG_PREFIX}${flagName}`,
  );
  if (stored === null) return { isOverridden: false };
  try {
    return { value: JSON.parse(stored), isOverridden: true };
  } catch {
    // Not valid JSON — return as raw string
  }
  return { value: stored as FlagValue, isOverridden: true };
};

export const getLocalStorageOverrides = (): Record<string, LDFlagValue> => {
  const overrides: Record<string, LDFlagValue> = {};
  for (let i = 0; i < globalThis.localStorage.length; i++) {
    const key = globalThis.localStorage.key(i);
    if (key?.startsWith(LS_FLAG_PREFIX)) {
      const flagName = key.slice(LS_FLAG_PREFIX.length);
      const override = getLocalStorageFlagOverride(flagName);
      if (override.isOverridden) {
        overrides[flagName] = override.value;
      }
    }
  }
  return overrides;
};

// Calls callback (via stable ref) whenever adsk:ff-override-change fires.
export const useOnOverrideChange = (callback: () => void): void => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  useEffect(() => {
    const handler = () => callbackRef.current();
    globalThis.addEventListener(FF_OVERRIDE_EVENT, handler);
    return () => globalThis.removeEventListener(FF_OVERRIDE_EVENT, handler);
  }, []);
};
